import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Card,
  CardContent,
  Typography,
  Chip,
  Box,
  Button,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Schedule as ScheduleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';

const getStatusConfig = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed':
      return {
        color: 'success',
        icon: <CheckCircleIcon sx={{ fontSize: 16, mr: 0.5 }} />,
        label: 'Completed',
      };
    case 'pending':
      return {
        color: 'warning',
        icon: <ScheduleIcon sx={{ fontSize: 16, mr: 0.5 }} />,
        label: 'Payment Incomplete',
      };
    case 'cancelled':
      return {
        color: 'error',
        icon: <CancelIcon sx={{ fontSize: 16, mr: 0.5 }} />,
        label: 'Cancelled',
      };
    default:
      return {
        color: 'default',
        icon: null,
        label: status,
      };
  }
};

function EnrollmentCard({ enrollment }) {
  const statusConfig = getStatusConfig(enrollment.status);

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 3,
        },
      }}
    >
      <Box
        sx={{
          background: 'linear-gradient(135deg, #2d5a42 0%, #1e4034 100%)',
          p: 2.5,
          color: 'white',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            gap: 1,
          }}
        >
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              lineHeight: 1.3,
              flex: 1,
            }}
          >
            {enrollment.program_name}
          </Typography>
          <Chip
            label={statusConfig.label}
            color={statusConfig.color}
            size="small"
            icon={statusConfig.icon}
            sx={{
              fontWeight: 600,
              '& .MuiChip-label': {
                px: 1,
              },
            }}
          />
        </Box>
      </Box>

      <CardContent sx={{ flexGrow: 1, p: 2.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              pb: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Enrollee
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>
              {enrollment.enrollee_name}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              pb: 1,
              borderBottom: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
              Enrolled on
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>
              {enrollment.created_at}
            </Typography>
          </Box>

          {enrollment.status?.toLowerCase() === 'pending' && (
            <Box>
              <Typography variant="body2" color="warning.dark" sx={{ mb: 1.5 }}>
                Checkout was not completed. No place is reserved until payment succeeds.
              </Typography>
              <Button component={RouterLink} to={`/programs/${enrollment.program_id}/enroll`}
                variant="outlined" color="warning" size="small" fullWidth>
                Retry Checkout
              </Button>
            </Box>
          )}

          {['completed', 'paid'].includes(enrollment.status?.toLowerCase()) &&
            enrollment.updated_at !== enrollment.created_at && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  Completed on
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>
                  {enrollment.updated_at}
                </Typography>
              </Box>
            )}
        </Box>
      </CardContent>
    </Card>
  );
}

export default EnrollmentCard;
